
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface Project {
  id: string
  title: string
  description: string
  category: string
  tech_stack: string[]
  image_url: string | null
  project_url: string | null
  is_featured: boolean
  display_order: number
  is_active: boolean
  problem?: string | null
  solution?: string | null
  impact?: string | null
}

interface ProjectFormProps {
  project?: Project | null
  onSave: () => void
  onCancel: () => void
}

const ProjectForm = ({ project, onSave, onCancel }: ProjectFormProps) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tech_stack: '',
    image_url: '',
    project_url: '',
    is_featured: false,
    display_order: 0,
    is_active: true,
    problem: '',
    solution: '',
    impact: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title,
        description: project.description,
        category: project.category,
        tech_stack: project.tech_stack.join(', '),
        image_url: project.image_url || '',
        project_url: project.project_url || '',
        is_featured: project.is_featured,
        display_order: project.display_order,
        is_active: project.is_active,
        problem: project.problem || '',
        solution: project.solution || '',
        impact: project.impact || ''
      })
    }
  }, [project])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const projectData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tech_stack: formData.tech_stack.split(',').map(tech => tech.trim()).filter(tech => tech),
        image_url: formData.image_url || null,
        project_url: formData.project_url || null,
        is_featured: formData.is_featured,
        display_order: formData.display_order,
        is_active: formData.is_active,
        problem: formData.problem || null,
        solution: formData.solution || null,
        impact: formData.impact || null
      }

      if (project) {
        const { error } = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', project.id)
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Project updated successfully",
        })
      } else {
        const { error } = await supabase
          .from('projects')
          .insert([projectData])
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Project created successfully",
        })
      }
      
      onSave()
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${project ? 'update' : 'create'} project`,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{project ? 'Edit Project' : 'Add New Project'}</CardTitle>
        <CardDescription>
          {project ? 'Update project information' : 'Create a new project entry'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="tech_stack">Tech Stack (comma separated)</Label>
            <Input
              id="tech_stack"
              value={formData.tech_stack}
              onChange={(e) => setFormData({ ...formData, tech_stack: e.target.value })}
              placeholder="React, Node.js, MongoDB"
            />
          </div>

          <ImageUpload
            currentImageUrl={formData.image_url}
            onImageUpload={(url) => setFormData({ ...formData, image_url: url })}
            onImageRemove={() => setFormData({ ...formData, image_url: '' })}
            label="Project Image"
            folder="projects"
          />
          
          <div>
            <Label htmlFor="project_url">Project URL</Label>
            <Input
              id="project_url"
              value={formData.project_url}
              onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
            />
          </div>

          {/* New Modal Content Fields */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">Modal Content (Optional)</h3>
            
            <div>
              <Label htmlFor="problem">Problem</Label>
              <Textarea
                id="problem"
                value={formData.problem}
                onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                placeholder="Describe the problem this project solved"
              />
            </div>
            
            <div>
              <Label htmlFor="solution">Solution</Label>
              <Textarea
                id="solution"
                value={formData.solution}
                onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                placeholder="Describe the solution implemented"
              />
            </div>
            
            <div>
              <Label htmlFor="impact">Impact</Label>
              <Textarea
                id="impact"
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                placeholder="Describe the impact and results achieved"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_featured"
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
            />
            <Label htmlFor="is_featured">Featured Project</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (project ? 'Update Project' : 'Create Project')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ProjectForm
